import React from 'react';

import {
  BarChart3,
  BookOpen,
  Bot,
  Brain,
  Briefcase,
  Code,
  Compass,
  Database,
  Factory,
  FileCheck,
  FileText,
  Globe,
  Heart,
  Lightbulb,
  MessageSquare,
  Microscope,
  Monitor,
  Network,
  PenTool,
  PieChart,
  RefreshCw,
  Rocket,
  Search,
  Target,
  Users,
  Wrench,
} from 'lucide-react';

import { Service, ServiceCategory } from '../types/serviceTypes';

export const services: Record<ServiceCategory, Service[]> = {
  digitales: [
    {
      id: 'presencia-digital',
      icon: React.createElement(Globe),
      title: 'Presencia Digital Profesional',
      description:
        'Establecimiento y optimización de la presencia digital de su empresa en las principales plataformas y directorios de servicios.',
      category: 'digitales',
      features: [
        'Registro en plataformas clave',
        'Optimización de perfiles',
        'Gestión de reputación online',
        'Estrategia de contenido',
        'Monitoreo de visibilidad',
      ],
      price: '$899',
      process: {
        steps: [
          {
            title: 'Análisis de Presencia Actual',
            description:
              'Evaluación detallada de la presencia digital existente y análisis de competidores.',
          },
          {
            title: 'Estrategia Personalizada',
            description:
              'Desarrollo de un plan estratégico adaptado a los objetivos específicos de su negocio.',
          },
          {
            title: 'Implementación',
            description:
              'Creación y optimización de perfiles en plataformas relevantes para su industria.',
          },
          {
            title: 'Monitoreo y Ajustes',
            description: 'Seguimiento continuo de resultados y optimización basada en datos.',
          },
        ],
      },
    },
    {
      id: 'predictive-analytics',
      icon: React.createElement(BarChart3),
      title: 'Análisis Predictivo',
      description:
        'Soluciones avanzadas de análisis de datos para anticipar tendencias y optimizar decisiones empresariales.',
      category: 'digitales',
      features: [
        'Modelos predictivos personalizados',
        'Análisis de tendencias en tiempo real',
        'Integración con fuentes de datos existentes',
        'Alertas predictivas automatizadas',
        'Dashboards interactivos',
      ],
      price: '$1299',
    },
    {
      id: 'automated-reports',
      icon: React.createElement(Database),
      title: 'Reportes Automatizados',
      description:
        'Sistema integral de generación y distribución de informes automatizados para una toma de decisiones eficiente.',
      category: 'digitales',
      features: [
        'Generación automática de informes',
        'Distribución programada',
        'Personalización de formatos',
        'Integración multiplataforma',
        'Análisis de datos en tiempo real',
      ],
      price: '$899',
    },
    {
      id: 'web-dev',
      icon: React.createElement(Monitor),
      title: 'Desarrollo Web y E-commerce',
      description: 'Sitios web modernos y tiendas online optimizadas para conversión.',
      category: 'digitales',
      features: [
        'Diseño responsivo',
        'SEO técnico',
        'Integración de pagos',
        'Google My Business',
        'Identidad visual básica',
      ],
      price: '$1499',
    },
    {
      id: 'automation-basic',
      icon: React.createElement(Bot),
      title: 'Automatización Básica',
      description: 'Automatización de procesos repetitivos para mejorar la eficiencia.',
      category: 'digitales',
      features: [
        'Automatización de tareas',
        'Integración de sistemas',
        'Reportes automáticos',
        'Flujos de trabajo',
      ],
      price: '$999',
    },
    {
      id: 'digital-marketing',
      icon: React.createElement(Search),
      title: 'Marketing Digital y SEO',
      description: 'Estrategias digitales para aumentar visibilidad y ventas.',
      category: 'digitales',
      features: [
        'SEO/SEM avanzado',
        'Estrategia de redes sociales',
        'Email marketing',
        'Análisis de datos',
        'Google Ads',
      ],
      price: '$799',
    },
  ],
  operaciones: [
    {
      id: 'erp-implementation',
      icon: React.createElement(Database),
      title: 'Implementación ERP',
      description: 'Sistemas de gestión empresarial integrados y personalizados.',
      category: 'operaciones',
      features: [
        'Gestión de inventario',
        'Contabilidad integrada',
        'CRM',
        'Reportes avanzados',
        'Facturación electrónica',
      ],
      price: '$2499',
    },
    {
      id: 'advanced-automation',
      icon: React.createElement(Bot),
      title: 'Automatización Avanzada',
      description: 'Soluciones de automatización industrial y procesos complejos.',
      category: 'operaciones',
      features: [
        'RPA',
        'Workflows complejos',
        'Integración IoT',
        'Monitoreo en tiempo real',
        'Dashboards personalizados',
      ],
      price: '$1999',
    },
    {
      id: 'business-intelligence',
      icon: React.createElement(PieChart),
      title: 'Business Intelligence',
      description: 'Análisis de datos y reportes para toma de decisiones.',
      category: 'operaciones',
      features: [
        'Dashboards personalizados',
        'Análisis predictivo',
        'KPIs en tiempo real',
        'Reportes automatizados',
        'Integración con fuentes de datos',
      ],
      price: '$1799',
    },
    {
      id: 'digital-transformation',
      icon: React.createElement(Rocket),
      title: 'Transformación Empresarial',
      description: 'Estrategia completa de transformación empresarial integral.',
      category: 'operaciones',
      features: [
        'Diagnóstico digital',
        'Roadmap de transformación',
        'Implementación de soluciones',
        'Capacitación de equipos',
        'Gestión del cambio',
      ],
      price: '$2999',
    },
  ],
  estrategicos: [
    {
      id: 'strategic-partner',
      icon: React.createElement(Compass),
      title: 'Socio Estratégico',
      description:
        'Acompañamiento integral y personalizado para la toma de decisiones estratégicas y el crecimiento sostenible de su negocio.',
      category: 'estrategicos',
      features: [
        'Asesoramiento ejecutivo continuo',
        'Análisis estratégico de mercado',
        'Planificación de crecimiento a largo plazo',
        'Optimización de procesos críticos',
        'Implementación de soluciones innovadoras',
      ],
      price: '$4999',
      process: {
        steps: [
          {
            title: 'Diagnóstico Estratégico',
            description:
              'Evaluación exhaustiva de la situación actual, fortalezas, debilidades y oportunidades de crecimiento.',
            details: [
              'Análisis exhaustivo de la infraestructura actual',
              'Identificación de puntos de mejora y oportunidades',
              'Evaluación de recursos necesarios',
              'Definición de objetivos medibles',
            ],
          },
          {
            title: 'Planificación Personalizada',
            description:
              'Desarrollo de un plan estratégico adaptado a los objetivos específicos de su organización.',
            details: [
              'Definición de alcance y objetivos del proyecto',
              'Asignación de recursos y responsabilidades',
              'Establecimiento de plazos y entregables',
              'Análisis de riesgos y plan de contingencia',
            ],
          },
          {
            title: 'Implementación Guiada',
            description:
              'Acompañamiento en la ejecución de iniciativas estratégicas con soporte continuo.',
            details: [
              'Desarrollo iterativo con entregas incrementales',
              'Pruebas continuas y control de calidad',
              'Despliegue progresivo de funcionalidades',
              'Capacitación y transferencia de conocimiento',
            ],
          },
          {
            title: 'Medición y Optimización',
            description:
              'Evaluación de resultados y ajuste continuo para maximizar el impacto de las estrategias implementadas.',
            details: [
              'Monitoreo de KPIs y métricas de rendimiento',
              'Análisis de resultados y retroalimentación',
              'Implementación de mejoras continuas',
              'Evaluación periódica de la satisfacción del cliente',
            ],
          },
        ],
      },
      deliverables: [
        'Plan estratégico personalizado',
        'Informes de análisis de mercado',
        'Roadmap de implementación',
        'Sesiones de asesoramiento ejecutivo',
        'Evaluaciones de progreso trimestrales',
      ],
      timeline: '12 meses con renovación anual',
    },
    {
      id: 'custom-development',
      icon: React.createElement(Code),
      title: 'Desarrollo a Medida',
      description: 'Soluciones tecnológicas completamente personalizadas.',
      category: 'estrategicos',
      features: [
        'Arquitectura empresarial',
        'Sistemas distribuidos',
        'Alta disponibilidad',
        'Escalabilidad',
        'Integración con sistemas legacy',
      ],
      price: '$3499',
    },
    {
      id: 'ai-solutions',
      icon: React.createElement(Brain),
      title: 'Soluciones de IA',
      description: 'Implementación de inteligencia artificial en procesos empresariales.',
      category: 'estrategicos',
      features: [
        'Machine Learning',
        'Procesamiento de lenguaje natural',
        'Visión computacional',
        'Análisis predictivo',
        'Automatización cognitiva',
      ],
      price: '$2999',
    },
    {
      id: 'industrial-automation',
      icon: React.createElement(Factory),
      title: 'Automatización Industrial',
      description: 'Sistemas completos de automatización para industria pesada.',
      category: 'estrategicos',
      features: ['Control de procesos', 'SCADA', 'PLC', 'Industria 4.0', 'IoT Industrial'],
      price: '$4999',
    },
    {
      id: 'enterprise-architecture',
      icon: React.createElement(Network),
      title: 'Arquitectura Empresarial',
      description: 'Diseño e implementación de arquitectura tecnológica corporativa.',
      category: 'estrategicos',
      features: [
        'Evaluación de infraestructura',
        'Diseño de arquitectura',
        'Implementación de soluciones',
        'Optimización de recursos',
        'Gestión de servicios TI',
      ],
      price: '$3999',
    },
  ],
  soporte: [
    {
      id: 'seo-marketing',
      icon: React.createElement(Search),
      title: 'Optimización SEO y Estrategia de Publicidad Digital',
      description: 'Posicionamiento web avanzado y estrategias publicitarias.',
      category: 'soporte',
      features: [
        'SEO técnico avanzado',
        'Estrategia de contenidos',
        'Campañas publicitarias',
        'Análisis de competencia',
        'Reportes de rendimiento',
      ],
      price: '$899',
    },
    {
      id: 'innovation-audit',
      icon: React.createElement(Microscope),
      title: 'Auditoría de Innovación Tecnológica',
      description: 'Diagnóstico completo de tecnología empresarial.',
      category: 'soporte',
      features: [
        'Evaluación de sistemas',
        'Análisis de procesos',
        'Recomendaciones técnicas',
        'Plan de modernización',
        'Roadmap tecnológico',
      ],
      price: '$1499',
    },
    {
      id: 'bi-dashboards',
      icon: React.createElement(BarChart3),
      title: 'Dashboards y Reportes BI',
      description: 'Paneles interactivos para monitoreo empresarial.',
      category: 'soporte',
      features: [
        'Visualización de datos',
        'KPIs personalizados',
        'Reportes automáticos',
        'Integración de fuentes',
        'Análisis predictivo',
      ],
      price: '$999',
    },
    {
      id: 'chatbots',
      icon: React.createElement(MessageSquare),
      title: 'Chatbots y Automatización',
      description: 'Automatización de atención al cliente.',
      category: 'soporte',
      features: [
        'Chatbots inteligentes',
        'Integración multicanal',
        'Respuestas automáticas',
        'Análisis de conversaciones',
        'Personalización avanzada',
      ],
      price: '$799',
    },
    {
      id: 'web-support',
      icon: React.createElement(Wrench),
      title: 'Soporte y Mantenimiento Web',
      description: 'Mantenimiento integral de plataforma web.',
      category: 'soporte',
      features: [
        'Monitoreo 24/7',
        'Actualizaciones de seguridad',
        'Copias de seguridad',
        'Optimización de rendimiento',
        'Soporte técnico',
      ],
      price: '$599',
    },
  ],
  asesoria: [
    {
      id: 'executive-consulting',
      icon: React.createElement(Briefcase),
      title: 'Consultoría Ejecutiva Integral',
      description: 'Asesoramiento estratégico personalizado para directivos y emprendedores.',
      category: 'asesoria',
      features: [
        'Diagnóstico de competencias directivas',
        'Plan de desarrollo profesional personalizado',
        'Acompañamiento en toma de decisiones',
        'Optimización de procesos de liderazgo',
        'Sesiones one-to-one con enfoque en resultados',
      ],
      price: '$2499',
    },
    {
      id: 'admin-management',
      icon: React.createElement(FileCheck),
      title: 'Gestión Administrativa Avanzada',
      description:
        'Optimización de procesos administrativos y documentales para mejorar la eficiencia operativa.',
      category: 'asesoria',
      features: [
        'Auditoría de procesos administrativos',
        'Diseño e implementación de flujos de trabajo',
        'Digitalización y gestión documental',
        'Implementación de sistemas de gestión',
        'Capacitación de personal administrativo',
      ],
      price: '$1899',
    },
    {
      id: 'project-management',
      icon: React.createElement(Target),
      title: 'Dirección de Proyectos Estratégicos',
      description: 'Gestión profesional de proyectos empresariales de alto impacto.',
      category: 'asesoria',
      features: [
        'Metodologías ágiles y tradicionales',
        'Planificación estratégica y alcance',
        'Gestión de recursos y presupuestos',
        'Control y seguimiento de hitos',
        'Evaluación de resultados y mejora',
      ],
      price: '$2999',
    },
  ],
  comunicacion: [
    {
      id: 'corporate-speaking',
      icon: React.createElement(MessageSquare),
      title: 'Oratoria y Presentaciones Corporativas',
      description:
        'Servicios profesionales de comunicación ejecutiva y presentaciones de alto impacto.',
      category: 'comunicacion',
      features: [
        'Técnicas avanzadas de oratoria',
        'Diseño de presentaciones efectivas',
        'Manejo de audiencias y escenarios',
        'Comunicación no verbal',
        'Presentaciones ejecutivas de alto impacto',
      ],
      price: '$1499',
    },
    {
      id: 'technical-writing',
      icon: React.createElement(FileText),
      title: 'Redacción Técnica Especializada',
      description: 'Desarrollo de documentación técnica profesional y contenidos especializados.',
      category: 'comunicacion',
      features: [
        'Documentación técnica de productos',
        'Manuales técnicos y de usuario',
        'White papers y casos de estudio',
        'Informes técnicos y de investigación',
        'Optimización SEO de contenidos técnicos',
      ],
      price: '$1299',
    },
    {
      id: 'commercial-proposals',
      icon: React.createElement(PenTool),
      title: 'Desarrollo de Propuestas Comerciales',
      description: 'Elaboración estratégica de propuestas y documentación comercial persuasiva.',
      category: 'comunicacion',
      features: [
        'Estructuración estratégica de propuestas',
        'Estructuración de propuestas ganadoras',
        'Análisis de necesidades del cliente',
        'Storytelling corporativo',
        'Seguimiento y optimización de propuestas',
      ],
      price: '$1699',
    },
  ],
  investigacion: [
    {
      id: 'market-research',
      icon: React.createElement(Microscope),
      title: 'Investigación de Mercados y Competencia',
      description:
        'Análisis exhaustivo de mercados, competidores y tendencias para la toma de decisiones estratégicas.',
      category: 'investigacion',
      features: [
        'Investigación de mercados',
        'Análisis de tendencias sectoriales',
        'Estudios de comportamiento del consumidor',
        'Identificación de oportunidades de mercado',
        'Benchmarking competitivo',
      ],
      price: '$2499',
    },
    {
      id: 'innovation-projects',
      icon: React.createElement(Lightbulb),
      title: 'Gestión Integral de Proyectos de Innovación',
      description:
        'Dirección y ejecución de proyectos de innovación desde la conceptualización hasta la implementación.',
      category: 'investigacion',
      features: [
        'Metodologías de Design Thinking',
        'Gestión de proyectos de I+D',
        'Prototipado y validación',
        'Estudios de factibilidad técnica',
        'Implementación de soluciones innovadoras',
      ],
      price: '$3499',
    },
    {
      id: 'business-development',
      icon: React.createElement(Target),
      title: 'Análisis de Viabilidad y Desarrollo de Negocios',
      description:
        'Evaluación y desarrollo de oportunidades de negocio con enfoque en innovación y sostenibilidad.',
      category: 'investigacion',
      features: [
        'Estudios de viabilidad comercial',
        'Análisis de mercados potenciales',
        'Desarrollo de modelos de negocio',
        'Evaluación de riesgos y oportunidades',
        'Planificación estratégica',
      ],
      price: '$2999',
    },
  ],
  formacion: [
    {
      id: 'corporate-training',
      icon: React.createElement(BookOpen),
      title: 'Programas de Formación Corporativa',
      description:
        'Programas de capacitación personalizados para desarrollar competencias clave en la organización.',
      category: 'formacion',
      features: [
        'Diagnóstico de necesidades formativas',
        'Metodología hands-on',
        'Evaluación de competencias digitales',
        'Capacitación de equipos',
        'Seguimiento de resultados',
      ],
      price: '$1999',
    },
    {
      id: 'specialized-workshops',
      icon: React.createElement(Users),
      title: 'Talleres Prácticos Especializados',
      description: 'Talleres intensivos enfocados en habilidades específicas y nuevas tecnologías.',
      category: 'formacion',
      features: [
        'Metodologías ágiles y tradicionales',
        'Herramientas digitales avanzadas',
        'Prácticas con casos reales',
        'Sesiones interactivas',
        'Evaluación de aprendizaje',
      ],
      price: '$1499',
    },
    {
      id: 'executive-mentoring',
      icon: React.createElement(Compass),
      title: 'Mentorías Ejecutivas Tecnológicas',
      description: 'Acompañamiento personalizado para directivos en transformación digital.',
      category: 'formacion',
      features: [
        'Evaluación de competencias digitales',
        'Plan de desarrollo personalizado',
        'Sesiones one-to-one',
        'Seguimiento de objetivos',
        'Recursos especializados',
      ],
      price: '$2499',
    },
    {
      id: 'business-ai',
      icon: React.createElement(Brain),
      title: 'Inteligencia Artificial Empresarial',
      description: 'Formación especializada en aplicación de IA en contextos empresariales.',
      category: 'formacion',
      features: [
        'Fundamentos de IA y ML',
        'Casos de uso empresariales',
        'Implementación práctica',
        'Gestión de proyectos de IA',
        'Evaluación de impacto',
      ],
      price: '$2999',
    },
  ],
  transformacion: [
    {
      id: 'business-diagnosis',
      icon: React.createElement(Search),
      title: 'Diagnóstico Integral de Negocios',
      description:
        'Evaluación exhaustiva del estado actual y potencial de mejora de la organización.',
      category: 'transformacion',
      features: [
        'Análisis de procesos clave',
        'Evaluación de sistemas',
        'Auditoría de Innovación Tecnológica',
        'Diagnóstico digital',
        'Benchmarking competitivo',
      ],
      price: '$3999',
    },
    {
      id: 'transformation-strategy',
      icon: React.createElement(Target),
      title: 'Estrategia de Transformación Empresarial',
      description: 'Diseño e implementación de estrategias de transformación organizacional.',
      category: 'transformacion',
      features: [
        'Plan de modernización',
        'Roadmap de transformación',
        'Gestión del cambio',
        'Capacitación de equipos',
        'Evaluación de resultados',
      ],
      price: '$4499',
    },
    {
      id: 'culture-transformation',
      icon: React.createElement(Users),
      title: 'Transformación de Cultura Organizacional',
      description:
        'Desarrollo de una cultura empresarial orientada a la innovación y mejora continua.',
      category: 'transformacion',
      features: [
        'Diagnóstico de cultura actual',
        'Diseño de cultura objetivo',
        'Programas de cambio cultural',
        'Liderazgo transformacional',
        'Medición de evolución cultural',
      ],
      price: '$3499',
    },
    {
      id: 'customer-experience',
      icon: React.createElement(Heart),
      title: 'Optimización de Experiencia del Cliente',
      description:
        'Mejora integral de la interacción con clientes en todos los puntos de contacto.',
      category: 'transformacion',
      features: [
        'Mapeo de journey del cliente',
        'Análisis de puntos de contacto',
        'Diseño de experiencias',
        'Implementación de mejoras',
        'Medición de satisfacción',
      ],
      price: '$2999',
    },
    {
      id: 'process-reengineering',
      icon: React.createElement(RefreshCw),
      title: 'Reingeniería y Optimización de Procesos',
      description: 'Rediseño fundamental de procesos para mejoras dramáticas en rendimiento.',
      category: 'transformacion',
      features: [
        'Análisis de procesos actuales',
        'Diseño de nuevos procesos',
        'Implementación de mejoras',
        'Automatización de flujos',
        'Medición de eficiencia',
      ],
      price: '$3799',
    },
  ],
} as const;
